// Dashboard page - role-based overview
// Reference: spec.md - personalized dashboard for each user role

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui';
import { api } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import type { IssueWithRelations, Property, User } from '@propman/shared';

interface DashboardStats {
  totalIssues: number;
  reportedIssues: number;
  inProgressIssues: number;
  completedIssues: number;
  assignedToMe: number;
}

interface PropertyWithUnits extends Property {
  unitCount: number;
}

// Status labels for display
const STATUS_LABELS = {
  reported: 'Reported',
  in_progress: 'In Progress',
  job_awarded_awaiting_appointment: 'Awaiting Appointment',
  completed: 'Completed',
};

const STATUS_COLORS = {
  reported: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  job_awarded_awaiting_appointment: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
};

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
}

export function DashboardPage() {
  const { user, organization } = useAuth();
  const [recentIssues, setRecentIssues] = useState<IssueWithRelations[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [properties, setProperties] = useState<PropertyWithUnits[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isManager = user?.role === 'manager' || user?.role === 'owner';

  useEffect(() => {
    async function loadDashboardData() {
      setIsLoading(true);
      try {
        // Load recent issues (different tabs based on role)
        const issuesPromise = api.get<{ issues: IssueWithRelations[]; meta: { total: number } }>(
          '/issues',
          { tab: isManager ? 'in_progress' : 'reported', pageSize: 5 }
        );

        // Load stats from different tabs
        const [inProgressRes, reportedRes, completedRes, assignedRes] = await Promise.all([
          api.get<{ meta: { total: number } }>('/issues', { tab: 'in_progress', pageSize: 1 }),
          api.get<{ meta: { total: number } }>('/issues', { tab: 'reported', pageSize: 1 }),
          api.get<{ meta: { total: number } }>('/issues', { tab: 'completed', pageSize: 1 }),
          isManager
            ? api.get<{ meta: { total: number } }>('/issues', { tab: 'your_tasks', pageSize: 1 })
            : Promise.resolve({ meta: { total: 0 } }),
        ]);

        setStats({
          totalIssues: inProgressRes.meta.total + completedRes.meta.total,
          reportedIssues: reportedRes.meta.total,
          inProgressIssues: inProgressRes.meta.total,
          completedIssues: completedRes.meta.total,
          assignedToMe: assignedRes.meta.total,
        });

        const issuesRes = await issuesPromise;
        setRecentIssues(issuesRes.issues);

        // Manager-specific data
        if (isManager) {
          const [propertiesRes, pendingUsersRes] = await Promise.all([
            api.get<{ properties: PropertyWithUnits[] }>('/properties'),
            api.get<{ users: User[] }>('/users/pending'),
          ]);
          setProperties(propertiesRes.properties.slice(0, 4));
          setPendingUsers(pendingUsersRes.users);
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadDashboardData();
  }, [isManager]);

  if (isLoading) {
    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Welcome header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.name?.split(' ')[0] || 'User'}
        </h1>
        <p className="mt-1 text-gray-600">
          {organization?.name || 'Your Property Management Portal'}
        </p>
      </div>

      {/* Quick actions */}
      <div className="mb-8">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
          Quick Actions
        </h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/issues/new">
            <Button>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Report Issue
            </Button>
          </Link>
          <Link to="/issues">
            <Button variant="secondary">
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View All Issues
            </Button>
          </Link>
          {isManager && (
            <>
              <Link to="/properties">
                <Button variant="secondary">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Manage Properties
                </Button>
              </Link>
              <Link to="/users">
                <Button variant="secondary">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Manage Users
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="mb-8">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Overview
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {isManager ? (
              <>
                <StatCard
                  title="Active Issues"
                  value={stats.inProgressIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="blue"
                  link="/issues?tab=in_progress"
                />
                <StatCard
                  title="Assigned to Me"
                  value={stats.assignedToMe}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  }
                  color="purple"
                  link="/issues?tab=your_tasks"
                />
                <StatCard
                  title="New Reports"
                  value={stats.reportedIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  }
                  color="amber"
                  link="/issues?tab=reported"
                />
                <StatCard
                  title="Resolved"
                  value={stats.completedIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="green"
                  link="/issues?tab=completed"
                />
              </>
            ) : (
              <>
                <StatCard
                  title="My Issues"
                  value={stats.reportedIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  }
                  color="blue"
                  link="/issues?tab=reported"
                />
                <StatCard
                  title="In Progress"
                  value={stats.inProgressIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="amber"
                  link="/issues?tab=in_progress"
                />
                <StatCard
                  title="Resolved"
                  value={stats.completedIssues}
                  icon={
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                  color="green"
                  link="/issues?tab=completed"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent issues */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {isManager ? 'Active Issues' : 'Your Recent Issues'}
            </h2>
            <Link to="/issues" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200">
            {recentIssues.length === 0 ? (
              <div className="p-6 text-center">
                <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">No issues found</p>
                <Link to="/issues/new">
                  <Button size="sm" className="mt-3">Report an Issue</Button>
                </Link>
              </div>
            ) : (
              recentIssues.map((issue) => (
                <Link
                  key={issue.id}
                  to={`/issues/${issue.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{issue.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {issue.unit.property.name} - {issue.unit.identifier}
                      </p>
                    </div>
                    <div className="ml-4 flex flex-col items-end">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[issue.status]}`}>
                        {STATUS_LABELS[issue.status]}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        {formatRelativeTime(issue.createdAt)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Manager-specific panels */}
        {isManager ? (
          <>
            {/* Pending users */}
            {pendingUsers.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Pending Approvals
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {pendingUsers.length}
                    </span>
                  </h2>
                  <Link to="/users?tab=pending" className="text-sm text-primary-600 hover:text-primary-700">
                    View all
                  </Link>
                </div>
                <div className="divide-y divide-gray-200">
                  {pendingUsers.slice(0, 5).map((pendingUser) => (
                    <div key={pendingUser.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {pendingUser.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">{pendingUser.name}</p>
                          <p className="text-xs text-gray-500">{pendingUser.email}</p>
                        </div>
                      </div>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        {pendingUser.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Properties overview - show if no pending users or alongside */}
            {(pendingUsers.length === 0 || properties.length > 0) && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Properties</h2>
                  <Link to="/properties" className="text-sm text-primary-600 hover:text-primary-700">
                    Manage
                  </Link>
                </div>
                <div className="divide-y divide-gray-200">
                  {properties.length === 0 ? (
                    <div className="p-6 text-center">
                      <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">No properties yet</p>
                      <Link to="/properties">
                        <Button size="sm" className="mt-3">Add Property</Button>
                      </Link>
                    </div>
                  ) : (
                    properties.map((property) => (
                      <Link
                        key={property.id}
                        to="/properties"
                        className="block p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {property.imageUrl ? (
                              <img
                                src={property.imageUrl}
                                alt={property.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-3 min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{property.name}</p>
                            <p className="text-xs text-gray-500 truncate">{property.address}</p>
                          </div>
                          <div className="ml-4">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {property.unitCount} units
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Leaseholder info panel */
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Need Help?</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">Report an Issue</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Having a problem in your unit? Report it and we&apos;ll get it fixed.
                    </p>
                    <Link to="/issues/new" className="mt-2 inline-flex text-sm text-primary-600 hover:text-primary-700">
                      Report now &rarr;
                    </Link>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">Stay Updated</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Track the status of your reported issues and get notified when there are updates.
                    </p>
                    <Link to="/issues" className="mt-2 inline-flex text-sm text-primary-600 hover:text-primary-700">
                      View your issues &rarr;
                    </Link>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">Account Settings</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Update your profile and notification preferences.
                    </p>
                    <Link to="/settings" className="mt-2 inline-flex text-sm text-primary-600 hover:text-primary-700">
                      Go to settings &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Stat card component
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'amber' | 'purple' | 'red';
  link: string;
}

const colorStyles = {
  blue: 'bg-blue-50 text-blue-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
  purple: 'bg-purple-50 text-purple-600',
  red: 'bg-red-50 text-red-600',
};

function StatCard({ title, value, icon, color, link }: StatCardProps) {
  return (
    <Link
      to={link}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-lg ${colorStyles[color]}`}>
          {icon}
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </Link>
  );
}

export default DashboardPage;
